/**
 * useAuth — authentication context, provider, and hook.
 *
 * Extracted from App.tsx to break the circular dependency:
 * App → RoleBasedDashboard → DesktopSidebar → useAuth ← was circular
 * Now: all three import from here — no cycle.
 */

import React, { useState, useEffect, createContext, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'

// ─── Types ──────────────────────────────────────────────────────────

export interface User {
  id?: number
  username: string
  role: string
  email?: string
  name?: string
  display_name?: string
}

interface AuthContextType {
  user: User | null
  login: (userData: User, token?: string) => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

// ─── Storage helpers ────────────────────────────────────────────────

const AUTH_STORAGE_KEYS = ['user', 'auth_token', 'user_role', 'user_email']

function clearAuthStorage() {
  // Clear BOTH localStorage and sessionStorage simultaneously.
  // login(), getCurrentUser(), and refreshUser() all write to both storages,
  // so stale data in either one is prevented at the source. This clear is
  // the safety net for shutdown — logout, 401, or token-not-found on mount.
  AUTH_STORAGE_KEYS.forEach(key => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
  localStorage.removeItem('user')
  sessionStorage.removeItem('user')
}

// ─── Context ────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// ─── Provider ───────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Validate session on mount
  useEffect(() => {
    // Check BOTH storages — login() writes to both, but on a new tab
    // sessionStorage is empty while localStorage has the valid token.
    const storedToken = sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token')
    const cachedUser = sessionStorage.getItem('user') || localStorage.getItem('user')

    if (!storedToken) {
      clearAuthStorage()
      setLoading(false)
      return
    }

    // Use cached user immediately so the app renders without delay
    if (cachedUser) {
      try {
        const parsed = JSON.parse(cachedUser) as User
        if (parsed && parsed.username) {
          setUser(parsed)
        }
      } catch {
        // Corrupted cache, ignore
      }
    }

    api.getCurrentUser()
      .then((data: any) => {
        const serverUser: User | null = data?.user ?? null
        if (serverUser) {
          setUser(serverUser)
          sessionStorage.setItem('user', JSON.stringify(serverUser))
          localStorage.setItem('user', JSON.stringify(serverUser))
        } else {
          clearAuthStorage()
        }
        setLoading(false)
      })
      .catch(() => {
        // Server validation failed — fall back to cached user if available
        // rather than immediately clearing auth and redirecting.
        // The 401 listener will handle expired tokens on actual API calls.
        if (cachedUser) {
          setLoading(false)
        } else {
          clearAuthStorage()
          setLoading(false)
          navigate('/login', { replace: true })
        }
      })
  }, [navigate])

  // ─── Logout event listener (from nav Sign Out button) ──────────
  useEffect(() => {
    const handleManualLogout = () => {
      logoutFn()
    }
    window.addEventListener('auth:manual-logout', handleManualLogout)
    return () => window.removeEventListener('auth:manual-logout', handleManualLogout)
  }, [])

  // Listen for global 401 events
  useEffect(() => {
    const handleUnauthorized = () => {
      clearAuthStorage()
      navigate('/login', { replace: true })
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [navigate])

  const login = (userData: User, token?: string) => {
    setUser(userData)
    sessionStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('user', JSON.stringify(userData))
    if (token) {
      // Write to BOTH storages — api.ts reads localStorage FIRST (line 18).
      // Without this, a stale localStorage token from a previous session would
      // be picked over a fresh sessionStorage token, causing a permanent 401 loop.
      sessionStorage.setItem('auth_token', token)
      localStorage.setItem('auth_token', token)
    }
  }

  const logoutFn = async () => {
    try {
      await api.logout()
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      clearAuthStorage()
      navigate('/', { replace: true })
    }
  }

  const refreshUser = async () => {
    try {
      const data: any = await api.getCurrentUser()
      const serverUser: User | null = data?.user ?? null
      if (serverUser) {
        setUser(serverUser)
        sessionStorage.setItem('user', JSON.stringify(serverUser))
        localStorage.setItem('user', JSON.stringify(serverUser))
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={{ user, login, logout: logoutFn, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}
